/**
 * Money, duration and token-count formatters for AI cost surfaces.
 *
 * Direct `Intl` calls (cached), not catalog formatters: these values sit in
 * tight metadata rows with no sentence to embed a `{x, format}` token in.
 *
 *   formatUsd(0.0004)             → "$0.0004"     (under a tenth of a cent: 4 decimals)
 *   formatUsd(0.004)              → "$0.004"      (under a cent: 3 decimals)
 *   formatUsd(0.0612)             → "$0.06"       (a cent or more: 2 decimals)
 *   formatUsd(0.00001)            → "<$0.0001"    (below the floor)
 *   formatEstimatedUsd(0.004)     → "≈ $0.004"
 *   formatEstimatedUsd(0.06, {partial:true}) → "≈ $0.06+"
 *   formatUsdRate(0.3)            → "$0.30"       (price per 1M tokens)
 *   formatDurationMs(125_000)     → "2m 5s"
 *   formatTokenCount(14_300)      → "14.3K"
 */

import { formatCompactNumber } from '@/i18n/formatters'

/** Smallest amount shown as a figure; anything positive below it reads "<$0.0001". */
export const COST_FLOOR_USD = 0.0001

const numberFormatCache = new Map<string, Intl.NumberFormat>()

const numberFormat = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat => {
  const key = `${locale}|${JSON.stringify(options)}`
  let cached = numberFormatCache.get(key)
  if (!cached) {
    cached = new Intl.NumberFormat(locale, options)
    numberFormatCache.set(key, cached)
  }
  return cached
}

const usd = (locale: string, digits: { min: number; max: number }) =>
  numberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits.min,
    maximumFractionDigits: digits.max,
  })

const usdFormatFor = (
  value: number,
  locale: string,
): { format: Intl.NumberFormat; value: number } => {
  // Round to each tier's grid first so 0.00996 reads "$0.01", not "$0.010",
  // and 0.00096 reads "$0.001", not "$0.0010".
  const fine = Math.round(value * 10_000) / 10_000
  if (fine < 0.001) return { format: usd(locale, { min: 4, max: 4 }), value: fine }
  const mid = Math.round(value * 1000) / 1000
  if (mid < 0.01) return { format: usd(locale, { min: 3, max: 3 }), value: mid }
  return { format: usd(locale, { min: 2, max: 2 }), value }
}

/**
 * A USD amount with precision that follows its size: 2 decimals from one
 * cent up, 3 below it, 4 below a tenth of a cent, and "<$0.0001" under the
 * floor (a positive cost is never shown as "$0.00"). Negative or non-finite
 * input is not an amount.
 *
 * `partial` marks a total that leaves out unpriced calls with a "+" placed
 * straight after the NUMBER in the locale's own layout ("$0.06+" in English,
 * "0,06+ US$" in Spanish) rather than after the currency symbol.
 */
export function formatUsd(
  amount: number,
  locale: string,
  { partial = false }: { partial?: boolean } = {},
): string {
  if (!Number.isFinite(amount) || !locale) return ''
  const value = Math.max(0, amount)
  if (value === 0) return withMarker(usd(locale, { min: 2, max: 2 }), 0, '', partial)
  const fine = Math.round(value * 10_000) / 10_000
  if (fine < COST_FLOOR_USD) {
    return withMarker(usd(locale, { min: 4, max: 4 }), COST_FLOOR_USD, '<', partial)
  }
  const { format, value: rounded } = usdFormatFor(value, locale)
  return withMarker(format, rounded, '', partial)
}

/** Formats `value`, putting `prefix` first and "+" right after the number when `partial`. */
function withMarker(
  format: Intl.NumberFormat,
  value: number,
  prefix: string,
  partial: boolean,
): string {
  if (!partial) return `${prefix}${format.format(value)}`
  const parts = format.formatToParts(value)
  let lastNumber = -1
  parts.forEach((part, index) => {
    if (part.type === 'integer' || part.type === 'fraction') lastNumber = index
  })
  return `${prefix}${parts
    .map((part, index) => (index === lastNumber ? `${part.value}+` : part.value))
    .join('')}`
}

/**
 * An estimated cost: "≈ $0.004". `partial` appends "+" for a total that
 * leaves out calls with no known price. Below the floor the figure already
 * says "less than", so no "≈" is added.
 */
export function formatEstimatedUsd(
  amount: number,
  locale: string,
  { partial = false }: { partial?: boolean } = {},
): string {
  const text = formatUsd(amount, locale, { partial })
  if (!text) return ''
  return text.startsWith('<') ? text : `≈ ${text}`
}

/** A price per 1M tokens: at least 2 and at most 4 decimals ("$0.30", "$0.006", "$0.125"). */
export function formatUsdRate(rate: number, locale: string): string {
  if (!Number.isFinite(rate) || !locale) return ''
  return usd(locale, { min: 2, max: 4 }).format(Math.max(0, rate))
}

const unit = (locale: string, name: 'second' | 'minute' | 'hour') =>
  numberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'narrow' })

/**
 * Short duration — "9s", "2m 5s", "1h 5m". Whole seconds, a non-zero
 * duration never reads "0s", and a zero trailing part is dropped
 * ("2m", not "2m 0s"). Units follow the locale ("9 s" in Spanish).
 */
export function formatDurationMs(ms: number, locale: string): string {
  if (!Number.isFinite(ms) || !locale) return ''
  const total = ms <= 0 ? 0 : Math.max(1, Math.round(ms / 1000))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const parts: string[] = []
  if (hours > 0) {
    parts.push(unit(locale, 'hour').format(hours))
    if (minutes > 0) parts.push(unit(locale, 'minute').format(minutes))
  } else if (minutes > 0) {
    parts.push(unit(locale, 'minute').format(minutes))
    if (seconds > 0) parts.push(unit(locale, 'second').format(seconds))
  } else {
    parts.push(unit(locale, 'second').format(seconds))
  }
  return parts.join(' ')
}

/** A token count in compact form — "621", "14.3K", "1.2M". */
export function formatTokenCount(tokens: number, locale: string): string {
  return formatCompactNumber(tokens, locale)
}
